import { Menu } from '@ark-ui/react';
import { ReactNode, useMemo } from 'react';
import { Portal } from '../Portal';
import styles from './Dropdown.module.scss';

export type DropdownItemBase = {
  id: string;
  label: ReactNode;
  disabled?: boolean;
};

export type DropdownItemAction = DropdownItemBase & {
  type?: 'action';
  onClick?: () => void;
};

export type DropdownItemLink = DropdownItemBase & {
  type: 'link';
  onClick: () => void;
};

export type DropdownItem = DropdownItemAction | DropdownItemLink;

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  positioning?: 'left' | 'right';
  value?: string;
  onValueChange?: (value: string) => void;
  ariaLabel?: string;
  'data-testid': string;
}

export const Dropdown = ({
  trigger,
  items,
  positioning = 'left',
  value,
  onValueChange,
  ariaLabel,
  'data-testid': dataTestid,
}: DropdownProps) => {
  const handleItemClick = (item: DropdownItem) => {
    item.onClick?.();
    if (onValueChange) {
      onValueChange(item.id);
    }
  };

  const menuPositioning = useMemo(() => {
    const placement: 'bottom-start' | 'bottom-end' =
      positioning === 'left' ? 'bottom-start' : 'bottom-end';
    return { placement };
  }, [positioning]);

  const linkItems = items.filter((item) => item.type === 'link');
  const actionItems = items.filter((item) => item.type !== 'link');
  const hasLinks = linkItems.length > 0;
  const hasActions = actionItems.length > 0;

  return (
    <Menu.Root closeOnSelect positioning={menuPositioning}>
      <Menu.Trigger asChild data-testid={dataTestid} aria-label={ariaLabel}>
        {trigger}
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content className={styles['content']}>
            {linkItems.map((item) => (
              <Menu.Item
                key={item.id}
                value={item.id}
                disabled={item.disabled}
                className={styles['item']}
                onClick={() => handleItemClick(item)}
                data-testid={`${dataTestid}-item-${item.id}`}
                data-selected={value === item.id ? '' : undefined}
                aria-selected={value === item.id}
              >
                {item.label}
              </Menu.Item>
            ))}
            {hasLinks && hasActions && (
              <div
                className={styles['separator']}
                role="separator"
                data-testid={`${dataTestid}-separator`}
              />
            )}
            {actionItems.map((item) => (
              <Menu.Item
                key={item.id}
                value={item.id}
                disabled={item.disabled}
                className={styles['item']}
                onClick={() => handleItemClick(item)}
                data-testid={`${dataTestid}-item-${item.id}`}
                data-selected={value === item.id ? '' : undefined}
                aria-selected={value === item.id}
              >
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};
